import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Trash, Repeat, Phone } from "@phosphor-icons/react";

export default function AdminDonors() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [frequency, setFrequency] = useState(""); // "", "monthly", "one-time"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [fixingDates, setFixingDates] = useState(false);

  const load = () => api.get("/admin/donations", { params: { status: status || undefined, limit: 1000 } }).then((r) => setRows(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/donations/reconcile");
      const n = data?.reconciled?.length || 0;
      if (n > 0) toast.success(`${n} pending donation${n === 1 ? "" : "s"} confirmed with the gateway and marked successful.`);
      else toast.success(`Checked ${data?.checked ?? 0} pending donation(s) — none had confirmed payments yet.`);
      if (data?.errors?.length) toast.error(`${data.errors.length} couldn't be checked (see server logs).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const fixHistoricalDates = async () => {
    if (!window.confirm(
      "This corrects the recorded date/time of already-successful donations that were reconciled before the date fix (Donors list + dashboard reports only). It cannot fix historical data already reported to Meta Ads Manager, since Meta has no way to amend an event it already received. Continue?"
    )) return;
    setFixingDates(true);
    try {
      const { data } = await api.post("/admin/donations/fix-historical-dates");
      const n = data?.fixed?.length || 0;
      if (n > 0) toast.success(`Corrected the date on ${n} donation${n === 1 ? "" : "s"}.`);
      else toast.success(`Checked ${data?.checked ?? 0} donation(s) — none needed correcting.`);
      if (data?.errors?.length) toast.error(`${data.errors.length} couldn't be checked (see server logs).`);
      if ((data?.checked ?? 0) >= 300) toast.info("There may be more to fix — click the button again to continue.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    } finally {
      setFixingDates(false);
    }
  };

  const deleteRow = async (row) => {
    const label = row.donor_name || row.donor_email || row.id.slice(0, 8);
    if (!window.confirm(`Delete donation from "${label}" (${formatINR(row.amount)})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/donations/${row.id}`);
      setRows((r) => r.filter((x) => x.id !== row.id));
      toast.success("Donation deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (q) {
      const Q = q.toLowerCase();
      const hit = [r.donor_name, r.donor_email, r.donor_phone, r.receipt_no, r.campaign_title, r.payment_id, r.order_id]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(Q));
      if (!hit) return false;
    }
    if (frequency === "monthly" && !r.monthly) return false;
    if (frequency === "one-time" && r.monthly) return false;
    const when = (r.completed_at || r.created_at || "").slice(0, 10);
    if (fromDate && when && when < fromDate) return false;
    if (toDate && when && when > toDate) return false;
    return true;
  }), [rows, q, frequency, fromDate, toDate]);

  const summary = useMemo(() => {
    const success = filtered.filter((r) => r.status === "success");
    return {
      count: filtered.length,
      success: success.length,
      monthly: success.filter((r) => r.monthly).length,
      total: success.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    };
  }, [filtered]);

  const clearFilters = () => { setQ(""); setStatus(""); setFrequency(""); setFromDate(""); setToDate(""); };

  const exportCSV = () => {
    const header = ["Receipt", "Name", "Email", "Phone", "PAN", "Amount", "Status", "Monthly", "Gateway", "Campaign", "Anonymous", "UTM Source", "UTM Medium", "UTM Campaign", "Created", "Completed"];
    const rowsCsv = filtered.map((r) => [
      r.receipt_no, r.donor_name, r.donor_email, r.donor_phone, r.pan || "", r.amount, r.status, r.monthly ? "yes" : "no", r.gateway, r.campaign_title,
      r.anonymous ? "yes" : "no", r.utm?.utm_source || "", r.utm?.utm_medium || "", r.utm?.utm_campaign || "", r.created_at, r.completed_at || "",
    ]);
    const csv = [header, ...rowsCsv].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `seva-donors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">Donors</h1>
          <p className="text-[#56624F] mt-1">All donations across all campaigns</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reconcile} disabled={reconciling} className="btn-secondary !py-2 !px-4 text-sm" data-testid="reconcile-pending-btn">{reconciling ? "Checking..." : "Reconcile Pending"}</button>
          <button onClick={fixHistoricalDates} disabled={fixingDates} className="btn-secondary !py-2 !px-4 text-sm" data-testid="fix-historical-dates-btn" title="Corrects the recorded date on donations reconciled before the date fix">{fixingDates ? "Fixing..." : "Fix Historical Dates"}</button>
          <button onClick={exportCSV} className="btn-secondary !py-2 !px-4 text-sm" data-testid="export-csv-btn">Export CSV</button>
        </div>
      </div>

      {/* Filters */}
      <div className="seva-card">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-3 items-end">
          <div className="col-span-2 lg:col-span-3">
            <label className="block text-xs font-semibold text-[#56624F] uppercase tracking-widest mb-1">Search</label>
            <input placeholder="Name, email, phone, receipt..." value={q} onChange={(e) => setQ(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="donor-search" />
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#56624F] uppercase tracking-widest mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="donor-status-filter">
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#56624F] uppercase tracking-widest mb-1">Frequency</label>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="donor-frequency-filter">
              <option value="">All</option>
              <option value="monthly">Monthly</option>
              <option value="one-time">One-time</option>
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#56624F] uppercase tracking-widest mb-1">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="donor-from-date" />
          </div>
          <div className="lg:col-span-3">
            <label className="block text-xs font-semibold text-[#56624F] uppercase tracking-widest mb-1">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="donor-to-date" />
          </div>
          <div className="col-span-2 lg:col-span-12 flex items-center justify-between flex-wrap gap-2 pt-1">
            <div className="text-xs text-[#56624F]" data-testid="donor-summary">
              <span className="font-semibold text-[#1F2A1C]">{summary.count}</span> rows · <span className="font-semibold text-[#2F7A4A]">{summary.success}</span> successful · <span className="font-semibold text-[#2F7A4A] inline-flex items-center gap-1"><Repeat size={11} weight="fill" />{summary.monthly}</span> monthly · Total raised: <span className="font-semibold text-[#1F2A1C]">{formatINR(summary.total)}</span>
            </div>
            <button onClick={clearFilters} className="text-xs text-[#56624F] hover:text-[#1F2A1C] underline" data-testid="donor-clear-filters">Clear filters</button>
          </div>
        </div>
      </div>

      <div className="seva-card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-[#EEF4E9]">
              <tr className="text-left text-[#56624F]">
                <th className="py-3 px-4">Donor</th>
                <th className="py-3 px-4">Phone</th>
                <th className="py-3 px-4">Amount</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Freq.</th>
                <th className="py-3 px-4">Gateway</th>
                <th className="py-3 px-4">Campaign</th>
                <th className="py-3 px-4">UTM</th>
                <th className="py-3 px-4">When</th>
                <th className="py-3 px-4 text-right sticky right-0 bg-[#EEF4E9]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#DCE5D4]" data-testid={`donor-row-${r.id}`}>
                  <td className="py-3 px-4">
                    <div className="font-medium">{r.donor_name} {r.anonymous && <span className="text-xs text-[#56624F]">(anon)</span>}</div>
                    <div className="text-xs text-[#56624F]">{r.donor_email}</div>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {r.donor_phone ? (
                      <a href={`tel:${r.donor_phone}`} className="inline-flex items-center gap-1 text-[#1F2A1C] hover:text-[#4FAC39]" data-testid={`donor-phone-${r.id}`}>
                        <Phone size={12} weight="fill" />{r.donor_phone}
                      </a>
                    ) : <span className="text-[#56624F]">—</span>}
                  </td>
                  <td className="py-3 px-4 font-semibold">{formatINR(r.amount)}</td>
                  <td className="py-3 px-4">
                    <span className={
                      "text-xs px-2 py-1 rounded-full " + (
                        r.status === "success" ? "bg-[#E6F3DC] text-[#2F7A4A]" :
                        r.status === "pending" ? "bg-[#FFF4D9] text-[#8A6A00]" :
                        "bg-[#FDEAE5] text-[#B84A34]"
                      )
                    }>{r.status}</span>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {r.monthly ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E6F3DC] text-[#2F7A4A] font-semibold" data-testid={`donor-monthly-${r.id}`}>
                        <Repeat size={11} weight="fill" /> Monthly
                      </span>
                    ) : <span className="text-[#56624F]">One-time</span>}
                  </td>
                  <td className="py-3 px-4 uppercase text-xs">{r.gateway} <span className="text-[#56624F]">({r.gateway_mode})</span></td>
                  <td className="py-3 px-4 max-w-xs truncate">{r.campaign_title}</td>
                  <td className="py-3 px-4 text-xs text-[#56624F]">{r.utm?.utm_source || "direct"}</td>
                  <td className="py-3 px-4 text-xs text-[#56624F] whitespace-nowrap">{formatDateTime(r.completed_at || r.created_at)}</td>
                  <td className="py-3 px-4 text-right sticky right-0 bg-white">
                    <button onClick={() => deleteRow(r)} className="p-1.5 rounded-lg text-[#B84A34] hover:bg-[#FDEAE5]" title="Delete donation" data-testid={`delete-donor-${r.id}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={10} className="py-10 text-center text-[#56624F]">No donors match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
